import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    TextField,
    IconButton,
    Avatar,
    useTheme,
    alpha,
    keyframes,
    Button
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// IMPORT YOUR ID ENCODER UTILITY
import { encodeId } from '../utils/idEncoder';

// Point to the new Python AI Engine https://apistaging.workhorse.com.sg
const API_BASE = 'https://api.workhorse.com.sg/ai';

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
`;

// --- INTERFACES MATCHING PYTHON ENGINE ---
interface VehicleUI {
    match_score: number | null;
    price_rating: string | null;
    is_best_match: boolean;
    pills: string[];
}

interface Vehicle {
    system_id: string;
    vehicle_id: number;
    condition: string;
    brand_model: string;
    body_type: string;
    transmission: string;
    fuel: string;
    price: number;
    image_url: string;
    description: string;
    ui: VehicleUI;
}

interface Message {
    role: 'system' | 'ai' | 'user';
    content: string;
    vehicles?: Vehicle[]; // NEW: Attach matched vehicles to the message
}

const AiChatSection = () => {
    const theme = useTheme();
    const chatEndRef = useRef<HTMLDivElement>(null);

    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<Message[]>([
        {
            role: 'ai',
            content: 'Hello! I am your WorkHorse AI Assistant. Tell me your budget, preferred body type, and what you will be using the vehicle for!'
        }
    ]);

    // Auto-scroll to bottom of chat
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const formatPrice = (price: number) => {
        return price > 0 ? `$${price.toLocaleString()}` : 'POA';
    };

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMsg = input.trim();
        setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
        setInput('');
        setLoading(true);

        try {
            const response = await fetch(`${API_BASE}/ai-search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_prompt: userMsg })
            });

            if (!response.ok) throw new Error('Network response was not ok');
            
            const data = await response.json();
            const matchedVehicles: Vehicle[] = data.matched_vehicles || [];
            
            let replyText = "";
            if (matchedVehicles.length > 0) {
                replyText = `I found ${data.results_count} vehicles that might be a great fit for you. Here are the top matches:`;
            } else {
                replyText = `I couldn't find any exact matches for those criteria. Try broadening your search or adjusting your budget.`;
            }

            setMessages(prev => [...prev, {
                role: 'ai',
                content: replyText,
                vehicles: matchedVehicles // Store the vehicles in the message
            }]);

        } catch (error) {
            setMessages(prev => [...prev, { role: 'ai', content: '⚠️ Sorry, I am having trouble connecting to the AI server right now.' }]);
        } finally {
            setLoading(false);
        }
    };

    // --- CUSTOM MESSAGE FORMATTER ---
    const formatMessage = (text: string) => {
        if (!text) return "";
        let formattedText = text;
        formattedText = formattedText.replace(/\n/g, '<br/>');
        formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>');
        return formattedText;
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: theme.palette.mode === 'light' ? '#F9FAFB' : alpha(theme.palette.background.paper, 0.5),
                p: { xs: 2, md: 4 }
            }}
        >
            {/* Header Text */}
            <Box textAlign="center" mb={{ xs: 2, md: 3 }}>
                <Typography variant="overline" sx={{ fontWeight: 'bold', letterSpacing: 2, color: '#FF8E00' }}>
                    SMART ASSISTANT
                </Typography>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                    Need Help Finding a Vehicle?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '600px', mx: 'auto' }}>
                    Ask our AI about current market trends, specific vehicle specifications, or let it match you with the perfect commercial vehicle.
                </Typography>
            </Box>

            {/* Chat Interface Paper */}
            <Paper
                elevation={0}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    width: '100%',
                    maxWidth: '800px',
                    mx: 'auto',
                    minHeight: '450px',
                    maxHeight: '800px',
                    borderRadius: 4,
                    overflow: 'hidden',
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: '0 10px 40px -10px rgba(0,0,0,0.1)'
                }}
            >
                {/* Chat Header Gradient */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        color: 'white',
                        flexShrink: 0
                    }}
                >
                    <AutoAwesomeIcon />
                    <Typography variant="h6" fontWeight="bold">
                        WorkHorse AI
                    </Typography>
                </Box>

                {/* Chat Messages Area */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        p: { xs: 2, md: 3 },
                        bgcolor: theme.palette.background.paper
                    }}
                >
                    <Stack spacing={3}>
                        {messages.map((msg, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                                    gap: 2,
                                    alignItems: 'flex-start'
                                }}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: msg.role === 'user' ? '#1e293b' : '#fff',
                                        color: msg.role === 'user' ? '#fff' : '#FF8E00',
                                        border: msg.role === 'ai' ? '1px solid #FF8E00' : 'none',
                                        width: 35, height: 35,
                                        flexShrink: 0
                                    }}
                                >
                                    {msg.role === 'user' ? <PersonIcon fontSize="small" /> : <AutoAwesomeIcon fontSize="small" />}
                                </Avatar>

                                <Box sx={{ maxWidth: { xs: '90%', md: '80%' } }}>
                                    {/* Text Bubble */}
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            borderTopLeftRadius: msg.role === 'ai' ? 4 : 24,
                                            borderTopRightRadius: msg.role === 'user' ? 4 : 24,
                                            bgcolor: msg.role === 'user' ? '#f8fafc' : alpha('#FF8E00', 0.05),
                                            border: msg.role === 'user' ? `1px solid ${theme.palette.divider}` : '1px solid transparent',
                                            color: theme.palette.text.primary,
                                            mb: msg.vehicles && msg.vehicles.length > 0 ? 2 : 0
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ lineHeight: 1.6, fontSize: '0.95rem' }}
                                            dangerouslySetInnerHTML={{ __html: formatMessage(msg.content) }}
                                        />
                                    </Paper>

                                    {/* NEW: Render Matched Vehicles inside the chat */}
                                    {msg.vehicles && msg.vehicles.length > 0 && (
                                        <Stack spacing={2} sx={{ mt: 1 }}>
                                            {msg.vehicles.map((v, vIdx) => (
                                                <Paper key={vIdx} elevation={0} sx={{ 
                                                    p: 1.5, 
                                                    borderRadius: 3, 
                                                    border: '1px solid #e2e8f0',
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: 2,
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    bgcolor: '#fff'
                                                }}>
                                                    {v.ui.is_best_match && (
                                                        <Box sx={{ 
                                                            position: 'absolute', top: 0, left: 0, 
                                                            bgcolor: '#FFc107', color: '#000', 
                                                            fontSize: '0.6rem', fontWeight: 'bold', 
                                                            px: 1, py: 0.3, borderBottomRightRadius: 8, zIndex: 1 
                                                        }}>
                                                            BEST MATCH
                                                        </Box>
                                                    )}

                                                    <Box sx={{ width: { xs: '100%', sm: 120 }, height: 90, borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                                                        <img src={v.image_url || 'https://via.placeholder.com/300x200?text=No+Image'} alt={v.brand_model} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    </Box>

                                                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                                        <Typography variant="subtitle1" fontWeight="800" sx={{ mb: 0.2, lineHeight: 1.2 }}>
                                                            {v.brand_model}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                                                            {v.condition === 'New' ? 'New' : 'Used'} • {v.fuel} • {v.transmission}
                                                        </Typography>
                                                        
                                                        <Stack direction="row" alignItems="baseline" spacing={1} mb={1}>
                                                            <Typography variant="body1" fontWeight="900" color="primary.main">{formatPrice(v.price)}</Typography>
                                                            {v.ui.price_rating && (
                                                                <Typography variant="caption" sx={{ color: v.ui.price_rating === 'Great Price' ? '#22c55e' : '#f59e0b', fontWeight: 'bold' }}>
                                                                    {v.ui.price_rating}
                                                                </Typography>
                                                            )}
                                                        </Stack>

                                                        {/* Action Button utilizing encodeId */}
                                                        <Button 
                                                            variant="outlined" 
                                                            size="small"
                                                            href={`/used-vehicle-details/${encodeId(v.vehicle_id)}`}
                                                            target="_blank"
                                                            sx={{ 
                                                                borderRadius: 2, 
                                                                textTransform: 'none', 
                                                                fontWeight: 'bold',
                                                                alignSelf: 'flex-start',
                                                                borderColor: '#FF8E00',
                                                                color: '#FF8E00',
                                                                '&:hover': { bgcolor: alpha('#FF8E00', 0.1), borderColor: '#FF8E00' }
                                                            }}
                                                        >
                                                            View Details ↗
                                                        </Button>
                                                    </Box>

                                                    {/* Match Score */}
                                                    {v.ui.match_score && (
                                                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 60, borderLeft: '1px solid #eee', pl: 2 }}>
                                                            <Box sx={{ 
                                                                width: 45, height: 45, borderRadius: '50%', 
                                                                border: `3px solid ${v.ui.match_score > 90 ? '#22c55e' : '#84cc16'}`,
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                                                            }}>
                                                                <Typography variant="body2" fontWeight="bold" sx={{ color: v.ui.match_score > 90 ? '#22c55e' : '#84cc16' }}>
                                                                    {v.ui.match_score}%
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.6rem' }}>Match</Typography>
                                                        </Box>
                                                    )}
                                                </Paper>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>
                            </Box>
                        ))}

                        {/* Animated Typing Indicator */}
                        {loading && (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                                <Avatar sx={{ bgcolor: '#fff', color: '#FF8E00', border: '1px solid #FF8E00', width: 35, height: 35 }}>
                                    <AutoAwesomeIcon fontSize="small" />
                                </Avatar>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        px: 2,
                                        py: 2.5,
                                        borderRadius: 3,
                                        borderTopLeftRadius: 4,
                                        bgcolor: alpha('#FF8E00', 0.05),
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.8,
                                    }}
                                >
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#FF8E00', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both`, animationDelay: '-0.32s' }} />
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#FF8E00', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both`, animationDelay: '-0.16s' }} />
                                    <Box sx={{ width: 6, height: 6, bgcolor: '#FF8E00', borderRadius: '50%', animation: `${bounce} 1.4s infinite ease-in-out both` }} />
                                </Paper>
                            </Box>
                        )}
                        <div ref={chatEndRef} />
                    </Stack>
                </Box>

                {/* Chat Input Area */}
                <Box
                    sx={{
                        p: 2,
                        borderTop: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.background.paper,
                        flexShrink: 0
                    }}
                >
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            fullWidth
                            placeholder="e.g., I need a heavy truck under $80,000 for construction..."
                            variant="outlined"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '50px',
                                    bgcolor: theme.palette.mode === 'light' ? '#f8fafc' : 'transparent',
                                }
                            }}
                        />
                        <IconButton
                            onClick={handleSend}
                            disabled={loading || !input.trim()}
                            sx={{
                                background: 'linear-gradient(135deg, #FFA100 0%, #FF8E00 100%)',
                                color: 'white',
                                width: 48,
                                height: 48,
                                '&:hover': { background: 'linear-gradient(135deg, #FF8E00 0%, #FFA100 100%)' },
                                '&.Mui-disabled': { background: '#e2e8f0', color: '#94a3b8' }
                            }}
                        >
                            <SendIcon />
                        </IconButton>
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
};

export default AiChatSection;